import React from "react";
import CategoriesLayout from "./CategoriesLayout";
import useTaskCategories from "hooks/tasks/useTaskCategories";
import { Spinner } from "react-bootstrap";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";

export default function TaskCategories() {
  const {
    categories,
    setCategories,
    addRootNode,
    addSubCategory,
    updateCategory,
    deleteCategory,
    loading,
  } = useTaskCategories();

  const insertCategory = (tree, newNode) => {
    // if adding root node
    if (!newNode.parentId) {
      return [...tree, { ...newNode }];
    }

    // if adding a sub category
    return tree.map((node) => {
      if (node.id === newNode.parentId) {
        return {
          ...node,
          children: [...(node.children || []), newNode],
        };
      }

      if (node.children?.length) {
        return {
          ...node,
          children: insertCategory(node.children, newNode),
        };
      }

      return node;
    });
  };

  const updateCategoryInTree = (tree, updatedNode) => {
    return tree.map((node) => {
      if (node.id === updatedNode.id) {
        // replace this node with the updated one
        return { ...node, ...updatedNode };
      }

      if (node.children?.length) {
        return {
          ...node,
          children: updateCategoryInTree(node.children, updatedNode),
        };
      }

      return node;
    });
  };

  const removeCategory = (tree, idToRemove) => {
    return tree
      .filter((node) => node.id !== idToRemove) // remove at this level
      .map((node) => ({
        ...node,
        children: node.children
          ? removeCategory(node.children, idToRemove)
          : [],
      }));
  };

  const handleAddRootCategory = async (payload) => {
    const newNode = await addRootNode(payload); // returns the added node
    setCategories((prev) => insertCategory(prev, newNode));
    return newNode;
  };

  const handleAddSubCategory = async (payload) => {
    const newNode = await addSubCategory(payload); // returns the added node
    setCategories((prev) => insertCategory(prev, newNode));
    return newNode;
  };

  const handleUpdateCategory = async (id, payload) => {
    const updatedNode = await updateCategory(id, payload); // returns updated node
    setCategories((prev) => updateCategoryInTree(prev, updatedNode));
    return updatedNode;
  };

  const handleDeleteCategory = async (id) => {
    await deleteCategory(id);
    setCategories((prev) => removeCategory(prev, id));
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spinner animation="border" variant="success" />
        <span className="ms-3" style={{ fontSize: "13.33px" }}>
          Loading...
        </span>
      </div>
    );
  }
  return (
    <>
      <WidgetSectionTitle
        icon="poll"
        title="Task Categories"
        subtitle="Manage and organize task categories to structure how tasks are grouped within the system."
        transform="shrink-2"
        className="mb-2 mt-3"
      />
      <CategoriesLayout
        categories={categories}
        addRootCategory={handleAddRootCategory}
        addSubCategory={handleAddSubCategory}
        updateCategory={handleUpdateCategory}
        deleteCategory={handleDeleteCategory}
      />
    </>
  );
}
